import React, { useState, useRef } from 'react';
import { api } from '../../services/api';
import styles from './AttachmentGallery.module.css';

/**
 * Full-screen swipeable gallery with pinch-to-zoom for mobile
 */
export default function AttachmentGallery({ attachments, user, canDelete, onDelete, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const touchStart = useRef(null);

  const current = attachments[currentIndex];

  const loadImage = async (att) => {
    if (imageUrls[att.id]) return imageUrls[att.id];
    if (att.driveUrl) return att.driveUrl;
    if (!att.s3Key) return null;
    setLoading(true);
    try {
      const { url } = await api.getDownloadUrl(user.credential, att.s3Key);
      setImageUrls(prev => ({ ...prev, [att.id]: url }));
      return url;
    } finally {
      setLoading(false);
    }
  };

  const getUrl = (att) => {
    if (att.driveUrl) return att.driveUrl;
    return imageUrls[att.id] || null;
  };

  // Load current image
  React.useEffect(() => {
    if (current) loadImage(current);
  }, [currentIndex]); // eslint-disable-line

  const goNext = () => setCurrentIndex(i => Math.min(i + 1, attachments.length - 1));
  const goPrev = () => setCurrentIndex(i => Math.max(i - 1, 0));

  const handleTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (!touchStart.current) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
    touchStart.current = null;
  };

  const handleDelete = async () => {
    if (!window.confirm('למחוק צרופה זו?')) return;
    try {
      await api.deleteAttachment(user.credential, current.id);
      if (onDelete) onDelete(current.id);
      if (attachments.length <= 1) onClose();
      else if (currentIndex >= attachments.length - 1) setCurrentIndex(i => i - 1);
    } catch (e) {
      alert('שגיאה במחיקה');
    }
  };

  const url = getUrl(current);
  const isImage = current?.contentType?.startsWith('image/');

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.gallery} onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>

        {/* Header */}
        <div className={styles.header}>
          <span className={styles.counter}>{currentIndex + 1} / {attachments.length}</span>
          <span className={styles.fileName}>{current?.fileName}</span>
          <div className={styles.headerActions}>
            {canDelete && <button className={styles.deleteBtn} onClick={handleDelete}>🗑️</button>}
            <button className={styles.closeBtn} onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {loading && <div className={styles.loading}>טוען...</div>}

          {!loading && url && isImage && (
            <img src={url} alt={current.fileName} className={styles.image} />
          )}

          {!loading && url && !isImage && (
            <div className={styles.filePreview}>
              <a href={url} target="_blank" rel="noopener noreferrer" className={styles.downloadLink}>
                📄 {current.fileName}<br />
                <span>לחץ לפתיחה</span>
              </a>
            </div>
          )}

          {!loading && !url && (
            <div className={styles.loading}>לא ניתן לטעון</div>
          )}
        </div>

        {/* Navigation arrows */}
        {attachments.length > 1 && (
          <>
            {currentIndex > 0 && <button className={`${styles.arrow} ${styles.arrowRight}`} onClick={goPrev}>›</button>}
            {currentIndex < attachments.length - 1 && <button className={`${styles.arrow} ${styles.arrowLeft}`} onClick={goNext}>‹</button>}
          </>
        )}
      </div>
    </div>
  );
}
